import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Calculator, BarChart3, Shield, Wifi, DollarSign, FileText, ChevronRight, Briefcase, Trophy, Map, CheckCircle2, Plane, Star, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { LeadFormModal } from '../components/common/LeadFormModal';
import { SEOHead } from '../components/common/SEOHead';
import { formatCurrency } from '../lib/utils';
import { sortCountriesBySEO } from '../lib/seoSorting';
import { safeFetch } from '../config/api';

// Featured City Card Component
const FeaturedCityCard = ({ city }) => {
  const totalCost = (city.rent_1br || 0) + (city.groceries_cost || 0) + (city.transport_cost || 0);
  const defaultImage = "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=600&q=80";
  
  return (
    <Link
      to={`/cities/${city.slug}`}
      className="group relative bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
      data-testid={`featured-city-${city.slug}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={city.image_url || defaultImage}
          alt={city.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* NomadIQ Badge */}
        <div className="absolute top-3 right-3 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-xs font-semibold text-blue-600 flex items-center gap-1">
          <Star className="w-3 h-3 fill-blue-600" />
          {city.nomad_score?.toFixed(1)}
        </div>
        
        {/* City Name & Country */}
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-xl font-bold text-white mb-1">{city.name}</h3>
          <p className="text-slate-200 text-sm flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {city.country_name}
          </p>
        </div>
      </div>
      
      {/* Stats */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-500 text-xs">Est. monthly cost</p>
            <p className="text-lg font-bold text-slate-900">{formatCurrency(totalCost)}</p>
          </div>
          <span className="text-blue-600 text-sm font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            Explore <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};

// Blog Card Component with Featured Image
const BlogGuideCard = ({ country }) => {
  const defaultImage = "https://images.unsplash.com/photo-1488085061387-422e29b40080?w=600&q=80";
  
  return (
    <Link
      to={`/move-to/${country.slug}`}
      className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-lg transition-all duration-300"
      data-testid={`guide-${country.slug}`}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={country.image_url || defaultImage}
          alt={country.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-3 left-3">
          <span className="text-3xl">{country.flag_emoji}</span>
        </div>
      </div>
      <div className="p-5">
        <span className="text-xs text-blue-600 font-medium uppercase tracking-wider">
          Complete Guide
        </span>
        <h3 className="text-lg font-semibold text-slate-900 mt-1 group-hover:text-blue-600 transition-colors">
          Move to {country.name} – Everything You Need to Know
        </h3>
      </div>
    </Link>
  );
};

export default function HomePage() {
  const [countries, setCountries] = useState([]);
  const [featuredCities, setFeaturedCities] = useState({ featured_destinations: [], featured_expat_cities: [] });
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const fetchFeatured = async () => {
      try {
        const featuredRes = await safeFetch(`/api/featured-cities`);
        if (cancelled) return false;
        if (featuredRes.ok) {
          const featuredData = await featuredRes.json();
          const hasAny =
            featuredData &&
            ((featuredData.featured_destinations?.length || 0) +
             (featuredData.featured_expat_cities?.length || 0)) > 0;
          if (hasAny) {
            if (!cancelled) setFeaturedCities(featuredData);
            return true;
          }
        }
      } catch (error) {
        console.warn('[HomePage] featured-cities fetch failed:', error?.message || error);
      }
      return false;
    };

    const fetchCountries = async () => {
      try {
        const countriesRes = await safeFetch(`/api/countries`);
        if (cancelled) return;
        if (countriesRes.ok) {
          const data = await countriesRes.json();
          if (Array.isArray(data) && data.length > 0 && !cancelled) {
            setCountries(sortCountriesBySEO(data));
          }
        }
      } catch (error) {
        console.warn('[HomePage] countries fetch failed:', error?.message || error);
      }
    };

    const run = async () => {
      // Independent fetches so one failure doesn't poison the other
      const [featuredOk] = await Promise.all([fetchFeatured(), fetchCountries()]);
      if (cancelled) return;
      setLoading(false);
      // Auto-retry featured-cities once after 1.5s if it came back empty (covers transient postMessage clone errors)
      if (!featuredOk) {
        retryTimer = setTimeout(() => { if (!cancelled) fetchFeatured(); }, 1500);
      }
    };
    run();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  const scoreExplanation = [
    { label: 'Cost of Living', weight: '25%', icon: DollarSign, color: 'text-emerald-600' },
    { label: 'Tax Benefits', weight: '20%', icon: FileText, color: 'text-blue-600' },
    { label: 'Visa Ease', weight: '15%', icon: Globe, color: 'text-purple-600' },
    { label: 'Safety', weight: '15%', icon: Shield, color: 'text-amber-600' },
    { label: 'Internet Quality', weight: '15%', icon: Wifi, color: 'text-cyan-600' },
    { label: 'Lifestyle', weight: '10%', icon: BarChart3, color: 'text-rose-600' },
  ];

  // Hero background image - digital nomad/remote work theme
  const heroImage = "https://images.unsplash.com/photo-1652088241116-5faf85b6c6aa?w=1920&q=80";

  return (
    <div className="min-h-screen bg-white">
      <SEOHead />
      <Navbar />

      {/* SECTION 1: Hero with Full Background Image */}
      <section className="relative min-h-[600px] md:min-h-[700px] overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={heroImage}
            alt="Global travel and relocation"
            className="w-full h-full object-cover"
            loading="eager"
          />
          {/* Dark Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50" />
        </div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-32 pb-16 md:pb-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-medium mb-6 border border-white/20">
              <Globe className="w-4 h-4" />
              Data-Driven Relocation Intelligence
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white tracking-tight mb-6">
              Move Smarter.<br />
              <span className="text-blue-400">Decide With Data.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-200 mb-8 leading-relaxed max-w-xl">
              Compare countries, explore cities, and plan your move abroad with confidence. Real data from 300+ destinations worldwide.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/countries">
                <Button className="h-12 md:h-14 px-6 md:px-8 bg-white text-slate-900 hover:bg-slate-100 text-base md:text-lg rounded-xl w-full sm:w-auto shadow-lg hover:shadow-xl transition-all" data-testid="hero-explore-btn">
                  Explore Destinations
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/tools/calculator">
                <Button variant="outline" className="h-12 md:h-14 px-6 md:px-8 text-base md:text-lg rounded-xl border-white/30 text-white hover:bg-white/10 w-full sm:w-auto backdrop-blur-sm" data-testid="hero-calculator-btn">
                  <Calculator className="mr-2 w-5 h-5" />
                  Calculate Cost
                </Button>
              </Link>
            </div>
            
            {/* Trust Indicators */}
            <div className="mt-10 flex flex-wrap gap-6 text-slate-300 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span><span className="font-semibold text-white">300+</span> cities worldwide</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span>Trusted by expats globally</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Popular Destinations */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="destinations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-3">
                <Star className="w-4 h-4" />
                Featured Destinations
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-2">
                Popular Destinations
              </h2>
              <p className="text-slate-600 text-base md:text-lg">
                Top-rated cities for digital nomads and expats
              </p>
            </div>
            <Link to="/cities" className="hidden md:flex items-center gap-1 text-blue-600 font-medium hover:text-blue-700 transition-colors">
              View all cities <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {(!featuredCities.featured_destinations.length && loading) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl h-72 animate-pulse" />
              ))}
            </div>
          ) : (!loading && featuredCities.featured_destinations.length === 0) ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-200" data-testid="featured-unavailable">
              <p className="text-slate-700 font-medium">Destinations are temporarily unavailable.</p>
              <p className="text-slate-500 text-sm mt-1">Please refresh — we&apos;re restoring the list.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCities.featured_destinations.slice(0, 6).map((city) => (
                <FeaturedCityCard key={city.slug} city={city} />
              ))}
            </div>
          )}

          <div className="mt-8 md:hidden text-center">
            <Link to="/cities">
              <Button variant="outline" className="w-full">View All Cities</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 3: Compare Countries Tool */}
      <section className="py-16 md:py-24" data-testid="compare-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Compare Countries Side by Side
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Make informed decisions by comparing up to 3 countries across key metrics like cost of living, taxes, visa requirements, and quality of life.
              </p>
              <ul className="space-y-4 mb-8">
                {['Cost of living breakdown', 'Tax structures compared', 'Visa requirements', 'Healthcare quality', 'Internet infrastructure'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-base">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/tools/compare">
                <Button className="h-12 px-6 bg-slate-900 text-white hover:bg-slate-800 rounded-xl" data-testid="compare-tool-btn">
                  Start Comparing
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            
            <div className="bg-gradient-to-br from-slate-100 to-blue-50 rounded-3xl p-6 md:p-8 border border-slate-200 shadow-sm">
              <div className="grid grid-cols-3 gap-4 mb-6">
                {countries.slice(0, 3).map((country, i) => (
                  <div key={i} className="text-center p-3 bg-white rounded-xl shadow-sm">
                    <span className="text-4xl mb-2 block">{country.flag_emoji}</span>
                    <span className="text-sm font-medium text-slate-900">{country.name}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">NomadIQ Score</span>
                <div className="grid grid-cols-3 gap-4 mt-3">
                  {countries.slice(0, 3).map((country, i) => (
                    <div key={i} className="text-center">
                      <span className="text-3xl font-bold text-slate-900">{country.nomad_score?.toFixed(1)}</span>
                      <span className="text-slate-500 text-sm">/10</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: Featured Calculators */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="calculators-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Featured Calculators
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Plan your move with precision using our data-driven tools
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/tools/calculator"
              className="group bg-white rounded-2xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all"
              data-testid="cost-calculator-card"
            >
              <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mb-6">
                <Calculator className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                Relocation Cost Calculator
              </h3>
              <p className="text-slate-600 mb-6">
                Estimate your monthly living costs based on city, family size, and lifestyle preferences.
              </p>
              <span className="text-blue-600 font-medium flex items-center gap-2">
                Try Calculator <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link
              to="/tools/salary-needed-calculator"
              className="group bg-white rounded-2xl p-8 border border-slate-200 hover:border-amber-300 hover:shadow-lg transition-all"
              data-testid="salary-calculator-card"
            >
              <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center mb-6">
                <Briefcase className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-amber-600 transition-colors">
                Salary Needed Calculator
              </h3>
              <p className="text-slate-600 mb-6">
                Estimate the monthly income required to live comfortably in different cities.
              </p>
              <span className="text-amber-600 font-medium flex items-center gap-2">
                Calculate Salary <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link
              to="/tools/compare"
              className="group bg-white rounded-2xl p-8 border border-slate-200 hover:border-emerald-300 hover:shadow-lg transition-all"
              data-testid="compare-tool-card"
            >
              <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-emerald-600 transition-colors">
                Country Comparison Tool
              </h3>
              <p className="text-slate-600 mb-6">
                Compare up to 3 countries side-by-side across all key relocation factors.
              </p>
              <span className="text-emerald-600 font-medium flex items-center gap-2">
                Compare Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 5: Popular Expat Cities */}
      <section className="py-16 md:py-24" data-testid="expat-cities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-3">
                <Trophy className="w-4 h-4" />
                Top Rated
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-2">
                Popular Expat Cities
              </h2>
              <p className="text-slate-600 text-base md:text-lg">
                Cities loved by expats for quality of life and value
              </p>
            </div>
            <Link to="/best-cities-for-expats" className="hidden md:flex items-center gap-1 text-emerald-600 font-medium hover:text-emerald-700 transition-colors">
              View rankings <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {(!featuredCities.featured_expat_cities.length && loading) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-slate-100 rounded-2xl h-72 animate-pulse" />
              ))}
            </div>
          ) : (!loading && featuredCities.featured_expat_cities.length === 0) ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-200" data-testid="expat-unavailable">
              <p className="text-slate-700 font-medium">Expat rankings are temporarily unavailable.</p>
              <p className="text-slate-500 text-sm mt-1">Please refresh — we&apos;re restoring the list.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCities.featured_expat_cities.slice(0, 6).map((city) => (
                <FeaturedCityCard key={city.slug} city={city} />
              ))}
            </div>
          )}

          <div className="mt-8 md:hidden text-center">
            <Link to="/best-cities-for-expats">
              <Button variant="outline" className="w-full">View All Rankings</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* NomadIQ Score Explanation */}
      <section className="py-16 md:py-24 bg-slate-900 text-white" data-testid="nomadiq-score-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              The NomadIQ Score
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Our proprietary scoring system weighs the factors that matter most to digital nomads and expats.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scoreExplanation.map((item, i) => (
              <div
                key={i}
                className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-colors"
              >
                <div className={`w-12 h-12 rounded-xl bg-slate-700 flex items-center justify-center mb-4 ${item.color}`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold mb-1">{item.label}</h3>
                <p className="text-slate-400 text-sm mb-3">Weight in score calculation</p>
                <span className="text-3xl font-bold text-blue-400">{item.weight}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: Popular Move Guides */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="guides-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-4">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-2">
                Popular Move Guides
              </h2>
              <p className="text-slate-600 text-base md:text-lg">
                In-depth guides to help you plan your relocation
              </p>
            </div>
            <Link to="/guides" className="hidden md:flex items-center gap-1 text-blue-600 font-medium hover:text-blue-700 transition-colors">
              View all guides <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {countries.slice(0, 3).map((country) => (
              <BlogGuideCard key={country.slug} country={country} />
            ))}
          </div>
          
          <div className="mt-8 md:hidden text-center">
            <Link to="/guides">
              <Button variant="outline" className="w-full">View All Guides</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Top Rankings Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 to-blue-50" data-testid="top-rankings-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-3">
                <Trophy className="w-4 h-4" />
                Data-Driven Rankings
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-2">
                Top Rankings for Expats
              </h2>
              <p className="text-slate-600">
                Explore our curated rankings to find the perfect destination
              </p>
            </div>
            <Link to="/rankings" className="hidden md:flex items-center gap-1 text-blue-600 font-medium hover:text-blue-700">
              View all rankings <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to="/cheapest-cities-for-digital-nomads"
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-300 hover:shadow-lg transition-all"
              data-testid="ranking-cheapest-nomads"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                Cheapest Cities for Digital Nomads
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Most affordable cities worldwide for remote workers.
              </p>
              <span className="text-emerald-600 font-medium flex items-center gap-1 text-sm">
                View ranking <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link
              to="/best-cities-for-expats"
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-amber-300 hover:shadow-lg transition-all"
              data-testid="ranking-best-expats"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                <Trophy className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors">
                Best Cities for Expats
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Top-rated cities based on quality of life metrics.
              </p>
              <span className="text-amber-600 font-medium flex items-center gap-1 text-sm">
                View ranking <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link
              to="/cheapest-cities-in-europe"
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all"
              data-testid="ranking-cheapest-europe"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                <Map className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                Cheapest Cities in Europe
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Budget-friendly European destinations.
              </p>
              <span className="text-blue-600 font-medium flex items-center gap-1 text-sm">
                View ranking <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link
              to="/best-cities-in-asia"
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-purple-300 hover:shadow-lg transition-all"
              data-testid="ranking-best-asia"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-purple-600 transition-colors">
                Best Cities in Asia
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Top Asian destinations for expats.
              </p>
              <span className="text-purple-600 font-medium flex items-center gap-1 text-sm">
                View ranking <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>

          <div className="mt-8 md:hidden text-center">
            <Link to="/rankings">
              <Button variant="outline" className="w-full">View All Rankings</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-3xl p-8 md:p-12 lg:p-16 text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full translate-x-1/2 translate-y-1/2" />
            
            <div className="relative">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
                Need Help Moving Abroad?
              </h2>
              <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
                Get personalized advice from our relocation experts. We'll help you navigate visas, find the right city, and plan your move.
              </p>
              <Button
                onClick={() => setShowLeadForm(true)}
                className="h-14 px-8 bg-white text-slate-900 hover:bg-slate-100 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                data-testid="cta-advisor-btn"
              >
                Talk to a Relocation Advisor
                <Plane className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <LeadFormModal
        isOpen={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        sourcePage="homepage"
      />
    </div>
  );
}
